import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../apiClient';
import { startUploader, retryNow, subscribeUploader, getOwnPendingCounts } from '../../services/localRecording/uploader';
import './MySessions.css';

// Rutan "inte riktigt färdig" visas först när det äldsta väntande klippet har
// väntat så här länge, så att en vanlig snabb uppladdning aldrig syns.
const WAITING_NOTICE_DELAY_MS = 2 * 60 * 1000;

/**
 * MySessions
 *
 * Visar en lista över alla sessioner för inloggad användare.
 * Tillåter radering av session samt navigering till MainContent
 * för att återuppta (paused) eller visa journal (completed).
 */
function MySessions() {
  const [sessions, setSessions] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCompleteAllModal, setShowCompleteAllModal] = useState(false);
  const [completingAll, setCompletingAll] = useState(false);
  // Lokal-först: lokala (ännu ej bekräftade) chunkar per session.
  const [localPending, setLocalPending] = useState({});   // { client_session_id: {pending, permanent_failure, oldest_pending_at} }

  const navigate = useNavigate();

  const refreshLocal = useCallback(async () => {
    try {
      setLocalPending(await getOwnPendingCounts());
    } catch (_) { /* IndexedDB kan saknas */ }
  }, []);

  useEffect(() => {
    fetchSessions();
    // Se till att uppladdaren snurrar och håll lokal-status färsk medan vyn är öppen.
    startUploader();
    refreshLocal();
    const unsub = subscribeUploader((ev) => {
      if (ev.type === 'pending_changed' || ev.type === 'session_completed') {
        refreshLocal();
        if (ev.type === 'session_completed') fetchSessions();
      }
    });
    const iv = setInterval(refreshLocal, 4000);
    return () => { unsub(); clearInterval(iv); };
  }, [refreshLocal]);

  const fetchSessions = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await apiClient.get('/recorder/sessions/my_sessions');
      if (Array.isArray(resp.data)) {
        setSessions(resp.data);
      } else {
        setError('Felaktigt svar från servern.');
      }
    } catch (err) {
      console.error('Fel vid hämtning av sessions:', err);
      setError('Kunde inte hämta sessions.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * handleResume(session)
   * Om status är "paused", navigera till /main-content med session_id,
   * så att man kan återuppta inspelningen.
   * Om status är "completed", navigera till /main-content med session_id,
   * men du kan ex. skicka en query-param "completed=1" om du vill öppna journalen direkt.
   */
  const handleOpenSession = (session) => {
    if (session.status === 'diktering') {
      // Navigate to SmartDictation for diktering sessions
      navigate(`/smart-dictation?session_id=${session.session_id}`);
    } else if (session.status === 'paused' || session.status === 'in_progress') {
      // Navigate to MainContent for regular recording sessions
      navigate(`/main-content?session_id=${session.session_id}`);
    } else if (session.status === 'completed') {
      // Navigate to MainContent for completed sessions
      navigate(`/main-content?session_id=${session.session_id}&completed=1`);
    } else {
      alert(`Okänt status: ${session.status}`);
    }
  };

  /**
   * handleDeleteSession(session_id)
   * Klarmarkerar sessionen (arkiverar den permanent).
   */
  const handleDeleteSession = async (session_id) => {
    // Skapa en custom modal istället för window.confirm
    const confirmDelete = () => {
      return new Promise((resolve) => {
        const modal = document.createElement('div');
        modal.className = 'confirm-modal-overlay';
        modal.innerHTML = `
          <div class="confirm-modal">
            <h3>Klarmarkera journal</h3>
            <p>Vill du klarmarkera denna journal?</p>
            <p class="warning-text">Detta arkiverar journalen permanent och den kan inte återställas.</p>
            <div class="modal-buttons">
              <button class="modal-button cancel-button" onclick="this.closest('.confirm-modal-overlay').remove(); window.resolveConfirm(false)">Avbryt</button>
              <button class="modal-button confirm-button" onclick="this.closest('.confirm-modal-overlay').remove(); window.resolveConfirm(true)">Klarmarkera</button>
            </div>
          </div>
        `;
        document.body.appendChild(modal);
        window.resolveConfirm = resolve;
      });
    };
    
    const confirmed = await confirmDelete();
    if (!confirmed) {
      return;
    }
    try {
      // OBS! Du måste skapa en DELETE-endpoint i backend, t.ex.:
      // DELETE /recorder/sessions/<session_id>
      await apiClient.delete(`/recorder/sessions/${session_id}`);
      // Ta bort ur listan i state:
      setSessions((prev) => prev.filter((s) => s.session_id !== session_id));
    } catch (err) {
      console.error('Fel vid radering av session:', err);
      alert('Kunde inte radera session.');
    }
  };

  /**
   * handleCompleteAll()
   * Klarmarkerar (arkiverar) samtliga sessioner på en gång. Kan inte ångras.
   */
  const handleCompleteAll = async () => {
    setCompletingAll(true);
    try {
      await apiClient.post('/recorder/sessions/complete_all');
      setSessions([]);
      setShowCompleteAllModal(false);
    } catch (err) {
      console.error('Fel vid klarmarkering av alla sessioner:', err);
      alert('Kunde inte klarmarkera alla sessioner.');
    } finally {
      setCompletingAll(false);
    }
  };

  // Sätt statustext
  const translateStatus = (status) => {
    switch (status) {
      case 'in_progress':
        return 'Pågående';
      case 'paused':
        return 'Pausad';
      case 'completed':
        return 'Avslutad';
      case 'diktering':
        return 'Diktering';
      default:
        return status;
    }
  };

  const handleRetryUpload = async () => {
    try { await retryNow(); } catch (_) { /* IndexedDB kan saknas */ }
    setTimeout(refreshLocal, 500);
  };

  // Leverans-badge (lokal-först): visar om journalen ännu färdigställs eller om
  // delar gått förlorade. Texterna talar om journalen, inte om tekniken.
  const deliveryBadge = (session) => {
    const local = session.client_session_id ? localPending[session.client_session_id] : null;
    if (local?.pending > 0) {
      return (
        <span className="delivery-badge sending" title="Inspelningen blir klar så snart som möjligt.">
          <i className="fas fa-sync fa-spin"></i> Färdigställs
        </span>
      );
    }
    if (local?.permanent_failure > 0) {
      return (
        <span className="delivery-badge failed" title="Journalen kan sakna delar av mötet. Kontakta support om något saknas.">
          <i className="fas fa-exclamation-triangle"></i> Ofullständig
        </span>
      );
    }
    if (session.has_permanent_failure) {
      return (
        <span className="delivery-badge failed" title={`${session.permanent_failed_chunks || ''} del(ar) kunde inte transkriberas. Journalen kan sakna delar av mötet.`}>
          <i className="fas fa-exclamation-triangle"></i> Delar saknas
        </span>
      );
    }
    if (session.processing_status === 'has_pending_retries' || (session.pending_chunks_count || 0) > 0) {
      return (
        <span className="delivery-badge sending" title="Delar av inspelningen bearbetas fortfarande i bakgrunden.">
          <i className="fas fa-sync fa-spin"></i> Bearbetar
        </span>
      );
    }
    return null;
  };

  // Journaler som faktiskt väntar: väntande (ej döda) klipp där det äldsta har
  // väntat längre än fördröjningen. Bygger medvetet inte på my_sessions (där
  // syns inte klarmarkerade sessioner) och inte på döda klipp, så rutan kan inte
  // fastna. Klipp äldre än 24 h städas bort av uppladdaren.
  const now = Date.now();
  const waitingCount = Object.values(localPending).filter(
    (c) => c.pending > 0 && c.oldest_pending_at && now - c.oldest_pending_at > WAITING_NOTICE_DELAY_MS
  ).length;

  return (
    <div className="my-sessions-container">
      <h2>Mina patientmöten</h2>

      {!loading && sessions.length > 0 && (
        <div className="complete-all-bar">
          <button
            className="complete-all-button"
            onClick={() => setShowCompleteAllModal(true)}
          >
            Klarmarkera alla
          </button>
        </div>
      )}

      {showCompleteAllModal && (
        <div className="confirm-modal-overlay">
          <div className="confirm-modal">
            <h3>Klarmarkera alla sessioner</h3>
            <p>Nu klarmarkerar du samtliga sessioner.</p>
            <p className="warning-text">
              Detta arkiverar alla journaler permanent och kan inte ångras.
            </p>
            <div className="modal-buttons">
              <button
                className="modal-button cancel-button"
                onClick={() => setShowCompleteAllModal(false)}
                disabled={completingAll}
              >
                Avbryt
              </button>
              <button
                className="modal-button confirm-button"
                onClick={handleCompleteAll}
                disabled={completingAll}
              >
                {completingAll ? 'Klarmarkerar...' : 'Klarmarkera alla'}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && <p>Laddar...</p>}
      {error && <p className="error-message">{error}</p>}

      {waitingCount > 0 && (
        <div className="local-unsent-banner">
          <div className="local-unsent-text">
            {waitingCount === 1
              ? 'En inspelning är inte riktigt färdig ännu. Den blir klar så snart som möjligt.'
              : `${waitingCount} inspelningar är inte riktigt färdiga ännu. De blir klara så snart som möjligt.`}
          </div>
          <button className="retry-upload-button" onClick={handleRetryUpload}>
            Slutför nu
          </button>
        </div>
      )}

      {!loading && sessions.length === 0 && waitingCount === 0 && (
        <p>Inga sessioner att visa.</p>
      )}

      {!loading && sessions.length > 0 && (
        <table className="sessions-table">
          <thead>
            <tr>
              <th>Skapad</th>
              <th>Status</th>
              <th>Kontaktorsak</th>
              <th>Journalmall</th>
              <th>Åtgärder</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((session) => (
              <tr key={session.session_id}>
                <td>
                  {session.created_at
                    ? new Date(session.created_at).toLocaleString('sv-SE')
                    : 'Okänd'}
                </td>
                <td>
                  <span className={`status-badge status-${session.status}`}>
                    {translateStatus(session.status)}
                  </span>
                  {deliveryBadge(session)}
                </td>
                <td>
                  {session.is_purged ? (
                    <span className="purge-badge purged" title="Transkript och genererat innehåll har gallrats enligt klinikens lagringstid. Journal sparad i journalsystemet påverkas inte.">
                      <i className="fas fa-eraser"></i> Gallrad
                    </span>
                  ) : (
                    <>
                      {session.session_name || session.summary || session.transcription_text}
                      {session.days_until_purge !== undefined && session.days_until_purge <= 7 && (
                        <span className="purge-badge warning" title={`Transkriptet gallras ${session.purge_at ? new Date(session.purge_at).toLocaleDateString('sv-SE') : 'snart'}. Spara journalen till journalsystemet innan dess.`}>
                          <i className="fas fa-clock"></i> Gallras om {session.days_until_purge} {session.days_until_purge === 1 ? 'dag' : 'dagar'}
                        </span>
                      )}
                    </>
                  )}
                </td>
                <td>
                  {session.template_name ? 
                    String(session.template_name) : 
                    (session.status === 'in_progress' ? 'Pågående' : 
                     session.status === 'paused' ? 'Pausad' : 
                     'Ej vald')
                  }
                </td>
                <td className="action-buttons">
                  <button
                    className="session-button open-session-button"
                    onClick={() => handleOpenSession(session)}
                  >
                    {session.status === 'completed'
                      ? 'Visa journal'
                      : session.status === 'diktering'
                      ? 'Fortsätt diktering'
                      : 'Återuppta'}
                  </button>
                  <button
                    className="session-button delete-session-button"
                    onClick={() => handleDeleteSession(session.session_id)}
                  >
                    Klarmarkera
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default MySessions;
